import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ChevronLeft, Users, ShieldCheck, Ban, UserCheck, Pencil,
  Layers, History, X,
} from "lucide-react";
import { prettify } from "./DeskDetail";

/* Headcount — HR-facing staff table + audit log.
 *
 *  Row actions: change role, change desk, deactivate/re-activate.
 *  Audit log lives on a second tab.
 */
export default function Headcount() {
  const [tab, setTab] = useState("staff");
  const [staff, setStaff] = useState([]);
  const [audit, setAudit] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [roles, setRoles] = useState(null);   // meta
  const [desks, setDesks] = useState(null);   // meta
  const [branches, setBranches] = useState([]);

  const load = useCallback(async () => {
    const [{ data: s }, { data: a }, { data: r }, { data: d }, { data: bs }] = await Promise.all([
      api.get(`/staff/mine${showInactive ? "?include_inactive=true" : ""}`),
      api.get("/staff/audit").catch(() => ({ data: { items: [] } })),
      api.get("/meta/agency-roles"),
      api.get("/meta/agency-types"),
      api.get("/office/branches").catch(() => ({ data: { branches: [] } })),
    ]);
    setStaff(s.items || []);
    setAudit(a.items || []);
    setCanEdit(!!s.can_edit);
    setRoles(r);
    setDesks(d);
    setBranches(bs.branches || []);
  }, [showInactive]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8"
         data-testid="headcount-page">
      <Link to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-white/50 hover:text-white transition">
        <ChevronLeft size={12} /> Back to Office
      </Link>
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Agency Office · HR
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1">
            Headcount
          </h1>
        </div>
        <label className="inline-flex items-center gap-2 text-[11px] text-white/60"
               data-testid="headcount-show-inactive-wrap">
          <input type="checkbox" checked={showInactive}
                 onChange={(e) => setShowInactive(e.target.checked)}
                 data-testid="headcount-show-inactive" />
          Show inactive
        </label>
      </div>

      <div className="mt-6 flex gap-1 border-b border-white/10">
        <TabBtn label="Staff" active={tab === "staff"} on={() => setTab("staff")} testid="headcount-tab-staff" />
        <TabBtn label={`Audit (${audit.length})`} active={tab === "audit"} on={() => setTab("audit")} testid="headcount-tab-audit" />
      </div>

      {tab === "staff" && (
        <div className="mt-6 space-y-2" data-testid="headcount-staff-list">
          {staff.length === 0 && (
            <div className="cs-card rounded-xl p-8 text-center text-sm text-white/45">
              No staff yet.
            </div>
          )}
          {staff.map((u) => (
            <StaffRow key={u.id} u={u} branches={branches} canEdit={canEdit}
                      onEdit={() => setEditing(u)} />
          ))}
        </div>
      )}

      {tab === "audit" && (
        <div className="mt-6 space-y-2" data-testid="headcount-audit-list">
          {audit.length === 0 && (
            <div className="cs-card rounded-xl p-8 text-center text-sm text-white/45">
              No changes yet.
            </div>
          )}
          {audit.map((a) => (
            <AuditRow key={a.id} a={a} />
          ))}
        </div>
      )}

      {editing && roles && desks && (
        <StaffEditModal u={editing} roles={roles} desks={desks} branches={branches}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function TabBtn({ label, active, on, testid }) {
  return (
    <button onClick={on}
      className={"px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-semibold border-b-2 transition " +
        (active ? "border-white text-white" : "border-transparent text-white/50 hover:text-white/80")}
      data-testid={testid}>
      {label}
    </button>
  );
}

function StaffRow({ u, branches, canEdit, onEdit }) {
  const isActive = u.is_active !== false;
  const branch = (branches || []).find((b) => b.id === u.branch_id);
  return (
    <div className={"cs-card rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap " +
                    (isActive ? "" : "opacity-60")}
         data-testid={`headcount-row-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-base uppercase tracking-tight truncate">{u.name}</div>
          {!isActive && (
            <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase bg-red-500/15 text-red-300 font-semibold">
              Inactive
            </span>
          )}
          {u.agency_role_label && (
            <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase bg-white/8 text-white/75 font-semibold"
                  data-testid={`headcount-role-${u.id}`}>
              {u.agency_role_label}
              {u.tier && <span className="ml-1 opacity-70">· {u.tier}</span>}
            </span>
          )}
          {u.desk_assignment && (
            <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase bg-indigo-500/15 text-indigo-300 font-semibold"
                  data-testid={`headcount-desk-${u.id}`}>
              {prettify(u.desk_assignment)}
            </span>
          )}
          {branch && (
            <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase bg-emerald-500/15 text-emerald-200 font-semibold"
                  data-testid={`headcount-branch-${u.id}`}>
              {branch.city_label}
            </span>
          )}
        </div>
        <div className="mt-1 text-[11.5px] text-white/50 truncate">{u.email}</div>
      </div>
      {canEdit && (
        <button onClick={onEdit}
          className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition inline-flex items-center gap-1.5"
          data-testid={`headcount-edit-${u.id}`}>
          <Pencil size={12} /> Edit
        </button>
      )}
    </div>
  );
}

function AuditRow({ a }) {
  const oldV = a.old_value === "" || a.old_value === null || a.old_value === false
    ? "—" : String(a.old_value);
  const newV = a.new_value === "" || a.new_value === null || a.new_value === false
    ? "—" : String(a.new_value);
  return (
    <div className="cs-card rounded-xl p-3 flex items-center gap-3 flex-wrap"
         data-testid={`audit-row-${a.id}`}>
      <History size={13} className="text-white/40" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-white/85">
          <span className="text-white/50">{a.actor_name}</span>
          <span className="mx-1 text-white/30">→</span>
          <span className="font-semibold">{a.target_name}</span>
        </div>
        <div className="text-[11px] text-white/55">
          <span className="text-white/40">{prettify(a.field)}:</span> {prettify(oldV)} → <span className="text-white/85">{prettify(newV)}</span>
          {a.reason && <span className="text-white/40 italic ml-2">({a.reason})</span>}
        </div>
      </div>
      <div className="text-[10px] tracking-[0.18em] uppercase text-white/40 tabular-nums">
        {new Date(a.created_at).toLocaleString()}
      </div>
    </div>
  );
}

function StaffEditModal({ u, roles, desks, branches, onClose, onSaved }) {
  const [form, setForm] = useState({
    agency_role: u.agency_role || "",
    desk_assignment: u.desk_assignment || "",
    is_active: u.is_active !== false,
    display_title: u.display_title || "",
    reason: "",
    branch_id: u.branch_id || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const allDeskKeys = Object.keys(desks?.desk_labels || {});

  const save = async () => {
    setBusy(true);
    try {
      // Two API calls — the staff PATCH doesn't know about branches
      await api.patch(`/staff/${u.id}`, {
        agency_role: form.agency_role,
        desk_assignment: form.desk_assignment,
        is_active: form.is_active,
        display_title: form.display_title,
        reason: form.reason,
      });
      if ((branches || []).length > 0
          && form.branch_id
          && form.branch_id !== (u.branch_id || "")) {
        await api.patch(`/office/staff/${u.id}/branch`, { branch_id: form.branch_id });
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
         data-testid="headcount-edit-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg cs-card rounded-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Edit staff</div>
            <div className="mt-0.5 font-display text-xl uppercase tracking-tight">{u.name}</div>
            <div className="text-[11px] text-white/45">{u.email}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-white/8" data-testid="hc-edit-close">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Agency Role</label>
            <select value={form.agency_role} onChange={(e) => upd("agency_role", e.target.value)}
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
              data-testid="hc-edit-role">
              <option value="">— none —</option>
              {(roles?.groups || []).map((g) => (
                <optgroup key={g.key} label={g.label}>
                  {g.roles.map((r) => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Desk Assignment</label>
            <select value={form.desk_assignment} onChange={(e) => upd("desk_assignment", e.target.value)}
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
              data-testid="hc-edit-desk">
              <option value="">— unassigned —</option>
              {allDeskKeys.map((k) => (
                <option key={k} value={k}>{desks.desk_labels[k]?.label || k}</option>
              ))}
            </select>
          </div>
          {(branches || []).length > 0 && (
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Branch</label>
              <select value={form.branch_id} onChange={(e) => upd("branch_id", e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                data-testid="hc-edit-branch">
                <option value="">— none / uses HQ —</option>
                {branches.map((b) => {
                  const optLabel = `${b.city_label} · ${b.label}${b.is_headquarters ? "  \u2605 HQ" : ""}`;
                  return <option key={b.id} value={b.id}>{optLabel}</option>;
                })}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Display Title (optional)</label>
            <input value={form.display_title} onChange={(e) => upd("display_title", e.target.value)}
              placeholder="e.g. Head of Creative — Mumbai"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
              data-testid="hc-edit-title" />
          </div>
          <label className="flex items-center gap-2 text-[12px] text-white/70"
                 data-testid="hc-edit-active-wrap">
            <input type="checkbox" checked={form.is_active}
                   onChange={(e) => upd("is_active", e.target.checked)}
                   data-testid="hc-edit-active" />
            Active on payroll
          </label>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Reason (audited)</label>
            <input value={form.reason} onChange={(e) => upd("reason", e.target.value)}
              placeholder="Promotion, transfer, exit…"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
              data-testid="hc-edit-reason" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose}
            className="h-10 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white/8">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
            className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
            data-testid="hc-edit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
